import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  Menu,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';

import { SodAdvancedFilters } from '../../../../../../../../shared/components';
import SodSearch from '../../../../../../../../shared/components/SodControls/SodSearch';
import {
  ABORT_ERROR,
  DEBOUNCE_TIME_FILTER_SEARCH,
  DEFAULT_AD_FILTER_PARAMS,
  VIOLATIONS_TAB_DETAILS_EXPANDER,
} from '../../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../../../shared/hooks';
import { detailsActions } from '../../../../../../../../shared/store/actions';
import { useGetEntitlementsMutation } from '../../../../../../../ViolationsTab/data-layer/violationsTabApi';

interface RiskConflictsHeaderProps {
  endpointNames: string[];
  isDisabled: boolean;
}

/*
 ** @fileoverview RiskConflictsHeader - Header component for risk conflicts display
 *
 * Provides search, filtering, and expand/collapse controls for the risk conflicts view.
 * Includes advanced filters, search functionality, and view control options.
 */
function RiskConflictsHeader({
  endpointNames,
  isDisabled,
}: RiskConflictsHeaderProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const { activeFilters, filters } = useTypedSelector(
    (state) => state.aag.details.entitlementConflicts.controls
  );

  // Filter out assigned entitlements from filters
  const filteredFilters = useMemo(
    () => filters.filter((filter) => filter.value !== 'assignedEntitlements'),
    [filters]
  );

  const [expanderOptions, setExpanderOptions] = useState(
    VIOLATIONS_TAB_DETAILS_EXPANDER.map((option) => ({
      ...option,
      isChecked: option.value === '1',
    }))
  );
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);
  const [fetchEntitlements] = useGetEntitlementsMutation();
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  const handleChange = (selectedOption: MenuOption) => {
    setExpanderOptions((currentOptions) =>
      currentOptions.map((option) => ({
        ...option,
        isChecked: option.value === selectedOption.value,
      }))
    );

    // Dispatch expand/collapse action based on selected option
    dispatch(
      detailsActions.setEntitlementConflictsExpandKey(selectedOption.value)
    );
  };

  const isClearBtn = useMemo(() => activeFilters.length > 0, [activeFilters]);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      // Abort should only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        Logger.log('//TODO: Endpoint names should be used', endpointNames);

        const controller = fetchEntitlements({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response as MenuOption[];

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [fetchEntitlements, endpointNames]
  );

  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(detailsActions.setEntitlementConflictsSearchKey(searchText));
    },
    [dispatch]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        dispatch(
          detailsActions.setEntitlementConflictsActiveFilters(newActiveFilters)
        );

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) => {
            if (filter.value === key) {
              return {
                ...filter,
                options: selectedOptions,
              };
            }

            return filter;
          })
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      dispatch(
        detailsActions.setEntitlementConflictsActiveFilters(newActiveFilters)
      );
    },
    [dispatch, activeFilters]
  );

  const handleClearBtn = useCallback(() => {
    dispatch(detailsActions.setEntitlementConflictsActiveFilters([]));
  }, [dispatch]);

  // Trigger expand on component mount
  useEffect(() => {
    dispatch(detailsActions.setEntitlementConflictsExpandKey('1'));
  }, [dispatch]);

  return (
    <Box
      tag='div'
      className='Sod-ViolationsTabDetailsConflicts-viewControlsWrapper'>
      <Box tag='div' className='Sod-ViolationsTabDetailsConflicts-viewControls'>
        <Typography
          kind='h3'
          className='Sod-ViolationsTabDetailsConflicts-viewControlsHeading'>
          {l('Conflicting Job Functions')}
        </Typography>
        <Box className='Sod-ViolationsTabDetailsConflicts-viewControlsButtons'>
          <Menu
            options={expanderOptions.map((option) => ({
              ...option,
              label: l(option.label),
            }))}
            onChange={handleChange}
            trigger={
              <ButtonSelect
                kind='rectangle'
                label={l('Expand/Collapse')}
                size='medium'
                prefixIcon={<Icon kind='ChevronExpandAll' />}
                isVisualCueEnabled={false}
                isDisabled={isDisabled}
              />
            }
          />
          <ButtonSelect
            label={l('Filter')}
            ariaLabel={l('Filter')}
            kind='rectangle'
            onClick={() => {
              setIsFilterBarOpen((prev) => !prev);
            }}
            isOpen={isFilterBarOpen}
            prefixIcon={<Icon kind='Filter' />}
            BadgeComp={
              isClearBtn ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : null
            }
            isDisabled={isDisabled}
            isVisualCueEnabled={false}
          />
          <SodSearch
            searchPlaceholder={l('Search')}
            inlineMesssage={l('Type more than 2 characters to see results.')}
            searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
            isDisabled={false}
            handleSearch={handleSearch}
          />
        </Box>
      </Box>
      <SodAdvancedFilters
        filters={filteredFilters}
        activeFilters={activeFilters}
        maxEnabledFilterCount={6}
        isClearBtn={isClearBtn}
        searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
        handleFilterChange={() => {}}
        handleFilterSearch={handleFilterSearch}
        handleOptionsClick={handleOptionsClick}
        handleClearBtn={handleClearBtn}
        className={
          isFilterBarOpen
            ? 'SodAdvancedFilters--isOpen'
            : 'SodAdvancedFilters--isClose'
        }
        isDisabled={isDisabled}
        isAddOrRemoveBtn={false}
      />
    </Box>
  );
}

export default memo(RiskConflictsHeader);
